import { useState, useEffect } from "react";
import { X, PhoneCall, ShieldCheck, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const PREFERRED_WINDOWS = [
  "9:30 AM – 11:00 AM",
  "11:00 AM – 12:30 PM",
  "12:30 PM – 2:00 PM",
  "2:00 PM – 3:30 PM",
  "3:30 PM – 5:30 PM",
  "Anytime during office hours (9:30 AM – 5:30 PM)",
];

const VISA_CATEGORIES = [
  "Student / Graduate Visa",
  "Skilled Migration",
  "Employer Sponsored / Work Visa",
  "Business / Investor Visa",
  "Partner / Family Visa",
  "Visitor Visa",
  "Appeals & Refusals",
  "Citizenship",
  "Other / Not sure yet",
];

export default function RequestCallbackModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    visa_category: "", preferred_time: "", message: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.visa_category || !form.preferred_time || !form.message) {
      toast.error("Please complete all fields before submitting.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent statement.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Callback request — ${form.visa_category}`,
        message: [
          form.message,
          `\nPreferred time: ${form.preferred_time}`,
          `Visa category: ${form.visa_category}`,
        ].join("\n"),
        consent: true,
        consent_timestamp: new Date().toISOString(),
      });
      setDone(true);
      toast.success("Callback requested — we'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="request-callback-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close callback modal"
        onClick={onClose}
        className="absolute inset-0 bg-[#29324A]/70 backdrop-blur-sm"
        data-testid="callback-backdrop"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <span aria-hidden className="block h-1 w-full bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />

        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          data-testid="callback-close-btn"
          className="absolute top-4 right-4 text-slate-400 hover:text-[#C43A89] transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-7 sm:p-10">
          {!done ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-[#C43A89]/10 p-2.5">
                  <PhoneCall className="h-5 w-5 text-[#C43A89]" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase">
                  Request a Callback
                </div>
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-[#29324A] tracking-tight leading-tight mb-3">
                We'll call you back — usually within 1 business day.
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Share a few details below so we can prepare for your call. All fields are required.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">Full name *</Label>
                    <Input
                      data-testid="callback-name"
                      required
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">Phone number *</Label>
                    <Input
                      data-testid="callback-phone"
                      required
                      type="tel"
                      placeholder="e.g. +61 4XX XXX XXX"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="rounded-none"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">Email *</Label>
                    <Input
                      data-testid="callback-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">Visa category *</Label>
                    <Select value={form.visa_category} onValueChange={(v) => update("visa_category", v)}>
                      <SelectTrigger data-testid="callback-category" className="rounded-none">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {VISA_CATEGORIES.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">Preferred time to call * <span className="text-slate-400 font-normal normal-case tracking-normal">(office hours: 9:30 AM – 5:30 PM)</span></Label>
                  <Select value={form.preferred_time} onValueChange={(v) => update("preferred_time", v)}>
                    <SelectTrigger data-testid="callback-time" className="rounded-none">
                      <SelectValue placeholder="When can we reach you?" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_WINDOWS.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-bold tracking-wider uppercase text-[#29324A] mb-1.5 block">A few details about your situation *</Label>
                  <Textarea
                    data-testid="callback-message"
                    required
                    rows={3}
                    placeholder="Briefly tell us your situation so we can prepare for the call…"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="rounded-none"
                  />
                </div>

                <ConsentCheckbox value={consent} onChange={setConsent} testid="callback-consent" showDisclaimer={false} />

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    data-testid="callback-submit"
                    disabled={submitting || !consent}
                    className="flex-1 bg-[#C43A89] hover:bg-[#a22b6e] text-white rounded-none py-6 font-semibold text-base btn-shimmer disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Request callback"}
                  </Button>
                  <div className="flex items-center gap-2 text-[12px] text-slate-500 px-2">
                    <Clock className="h-3.5 w-3.5 text-[#29324A]" />
                    <span>We'll call within <strong className="text-[#29324A]">1 business day</strong>.</span>
                  </div>
                </div>

                <div className="pt-2 flex items-center gap-2 text-[11px] text-slate-500">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#29324A]" />
                  Your details are kept confidential and used only to contact you about your enquiry.
                </div>
              </form>
            </>
          ) : (
            <div data-testid="callback-success" className="text-center py-6">
              <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-[#29324A] to-[#C43A89] flex items-center justify-center mb-5">
                <PhoneCall className="h-7 w-7 text-white" />
              </div>
              <h2 className="font-display text-2xl text-[#29324A] mb-2">Got it — we'll be in touch.</h2>
              <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                A member of our Registered Migration Agent team will call you back within 1 business day during your preferred window.
              </p>
              <Button
                type="button"
                onClick={onClose}
                data-testid="callback-close-success"
                className="mt-7 bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none px-7 py-3 text-xs font-bold tracking-wider uppercase"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
